/**
 * Legacy "heavy" POLYLINE family types (`POLYLINE`, `VERTEX`).
 *
 * Split out of `types.ts` (which is already at the 2000-line ceiling) as a
 * LEAF module: it imports its base types from `./dxfPrimitives` — a module
 * with no imports of its own — and NEVER from `./types`, so the graph edge
 * runs one way only, `types.ts` → here. Importing the base from `types.ts`
 * instead would close the cycle `types.ts` → this module → `types.ts`;
 * TypeScript erases a type-only cycle so nothing would crash, but the
 * repo's `no-circular` depcruise rule does not distinguish type edges from
 * value edges and fails CI on it (CLAUDE.md ПРИОРИТЕТ #2). `types.ts`
 * re-exports these names so external import paths are unchanged.
 *
 * # Source of truth
 *
 * Autodesk DXF reference, `POLYLINE` / `VERTEX` / `SEQEND` entities, plus
 * ezdxf 1.4.4 `entities/polyline.py` (`acdb_polyline` / `acdb_vertex`).
 * AutoCAD MCP is BANNED as a source.
 *
 * # Why this is NOT folded into `LWPOLYLINE`
 *
 * `LWPOLYLINE` (AcDbPolyline) is a strictly 2D, single-elevation, OCS-planar
 * primitive whose vertices are bare `(x, y[, bulge, widths])` tuples. A heavy
 * `POLYLINE` is a different AutoCAD object that `LWPOLYLINE` provably cannot
 * express:
 *
 *   - a 3D polyline (group 70 bit 8) has per-vertex Z,
 *   - a polygon mesh (bit 16) is an M×N grid of vertices,
 *   - a poly-face mesh (bit 64) is a vertex pool plus face records whose
 *     71/72/73/74 indices reference it (negative index = invisible edge),
 *   - curve-fit (bit 2) / spline-fit (bit 4) polylines interleave GENERATED
 *     vertices with the user's frame control points, distinguished per-vertex
 *     by group 70 bits 1/8/16,
 *   - every VERTEX is a full DXF entity with its own handle / owner / layer,
 *     which a flat `{x, y}` tuple has nowhere to keep.
 *
 * Flattening any of those into `LWPOLYLINE` would be exactly the
 * "упрощение" ПРИОРИТЕТ #1 forbids, so this is a second, genuinely distinct
 * typed primitive — not a second truth for the same one.
 */
import type {
  DxfPoint3D,
  DxfSeqendEntity,
  DxfSupportedEntityBase,
} from "./dxfPrimitives";

/**
 * DXF `POLYLINE` group 70 — polyline flags. Bit names verbatim from the
 * Autodesk DXF reference (POLYLINE entity, group code 70).
 */
export const DXF_POLYLINE_FLAG = {
  /** 1 — closed polyline, or (mesh) closed in the M direction. */
  CLOSED: 1,
  /** 2 — curve-fit vertices have been added. */
  CURVE_FIT: 2,
  /** 4 — spline-fit vertices have been added. */
  SPLINE_FIT: 4,
  /** 8 — this is a 3D polyline. */
  POLYLINE_3D: 8,
  /** 16 — this is a 3D polygon mesh. */
  POLYGON_MESH: 16,
  /** 32 — the polygon mesh is closed in the N direction. */
  CLOSED_N: 32,
  /** 64 — this is a poly-face mesh. */
  POLYFACE_MESH: 64,
  /** 128 — linetype pattern is generated continuously around the vertices. */
  CONTINUOUS_LINETYPE: 128,
} as const;

/**
 * DXF `VERTEX` group 70 — vertex flags. Verbatim from the Autodesk DXF
 * reference (VERTEX entity, group code 70).
 */
export const DXF_VERTEX_FLAG = {
  /** 1 — extra vertex created by curve-fitting. */
  CURVE_FIT_EXTRA: 1,
  /** 2 — curve-fit tangent defined for this vertex (group 50 is meaningful). */
  CURVE_FIT_TANGENT: 2,
  /** 8 — spline vertex created by spline-fitting. */
  SPLINE_VERTEX: 8,
  /** 16 — spline frame control point. */
  SPLINE_FRAME_CONTROL_POINT: 16,
  /** 32 — 3D polyline vertex. */
  POLYLINE_3D_VERTEX: 32,
  /** 64 — 3D polygon mesh vertex. */
  POLYGON_MESH_VERTEX: 64,
  /** 128 — poly-face mesh vertex (a FACE record when 71..74 are present). */
  POLYFACE_MESH_VERTEX: 128,
} as const;

/**
 * One `VERTEX` record of a heavy POLYLINE. A VERTEX is a full DXF entity
 * (own handle / owner / layer / linetype / colour), so it extends the same
 * base every other supported entity does rather than being a bare tuple.
 */
export type DxfVertexEntity = DxfSupportedEntityBase & {
  type: "VERTEX";
  geometry: {
    /** 10/20/30 — vertex location. In a 2D polyline this is OCS. */
    location: DxfPoint3D;
    /** 40 — starting width; absent ⇒ inherit the POLYLINE's group 40. */
    startWidth?: number;
    /** 41 — ending width; absent ⇒ inherit the POLYLINE's group 41. */
    endWidth?: number;
    /** 42 — bulge (tan of ¼ of the included arc angle); absent ⇒ 0. */
    bulge?: number;
    /** 70 — vertex flags, see {@link DXF_VERTEX_FLAG}. */
    flags?: number;
    /** 50 — curve-fit tangent direction (degrees); meaningful with bit 2. */
    curveFitTangent?: number;
    /**
     * 71/72/73/74 — poly-face mesh face record: the 1-based indices of the
     * vertices forming this face, in authored order. A NEGATIVE index means
     * the edge starting at that vertex is invisible (Autodesk DXF reference,
     * VERTEX group 71). Stored as an ordered array (never four booleans /
     * four scalars) so a triangular face (three indices) and a quad face
     * (four) are both expressible, and the authored order — which determines
     * the face winding — survives. Absent on a non-face vertex.
     */
    faceVertexIndices?: number[];
    /** 91 — persistent vertex identifier (same semantics as AcDbPolyline 91). */
    vertexId?: number;
  };
};

/**
 * A heavy `POLYLINE` with its `VERTEX` children and terminating `SEQEND`
 * folded in — the same shape the parser gives an `INSERT` that owns
 * `ATTRIB`s.
 */
export type DxfPolylineEntity = DxfSupportedEntityBase & {
  type: "POLYLINE";
  geometry: {
    /**
     * 66 — the obsolete "vertices follow" flag. AutoCAD always writes 1 and
     * the DXF reference marks it obsolete but still required. Retained
     * verbatim (never synthesised) so a file that omits it round-trips
     * without gaining a group it did not have.
     */
    verticesFollow?: number;
    /**
     * 10/20/30 — the "dummy point": X and Y are always 0 and Z is the
     * polyline's elevation (Autodesk DXF reference). Kept as the full point
     * rather than a bare elevation scalar so a file whose X/Y are not 0
     * round-trips unchanged.
     */
    elevation: DxfPoint3D;
    /** 70 — polyline flags, see {@link DXF_POLYLINE_FLAG}. */
    flags: number;
    /** 40 — default starting width for every vertex that omits its own 40. */
    defaultStartWidth?: number;
    /** 41 — default ending width for every vertex that omits its own 41. */
    defaultEndWidth?: number;
    /** 71 — polygon mesh M vertex count, or poly-face mesh vertex count. */
    meshMCount?: number;
    /** 72 — polygon mesh N vertex count, or poly-face mesh face count. */
    meshNCount?: number;
    /** 73 — smooth surface M density. */
    smoothSurfaceMDensity?: number;
    /** 74 — smooth surface N density. */
    smoothSurfaceNDensity?: number;
    /**
     * 75 — curves and smooth-surface type: 0 = none, 5 = quadratic B-spline,
     * 6 = cubic B-spline, 8 = Bezier surface.
     */
    surfaceType?: number;
    /** The owned `VERTEX` run, in file order. */
    vertices: DxfVertexEntity[];
    /**
     * The terminating `SEQEND`, retained whole (not just its handle, unlike
     * INSERT) so its own handle / owner / layer round-trip verbatim — the
     * data loss `dxfBridge.seqendPassthrough.ts` was written to work around.
     */
    seqend?: DxfSeqendEntity;
  };
};

/** Ordered VERTEX group codes that carry a poly-face face index. */
export const DXF_VERTEX_FACE_INDEX_CODES: readonly number[] = [71, 72, 73, 74];

/** Convenience predicate set for the four mesh/3D-discriminating 70 bits. */
export type DxfPolylineSubtype =
  | "2d"
  | "3d"
  | "polygon-mesh"
  | "polyface-mesh";

/**
 * Classify a heavy POLYLINE from its group-70 flags.
 *
 * The bit precedence is copied EXACTLY from ezdxf 1.4.4
 * `Polyline.get_mode()` — 3D polyline (8) first, then polygon mesh (16),
 * then poly-face mesh (64), else 2D — because that is the order that decides
 * which `100 AcDb…` subtype marker AutoCAD reads the body under. Reordering
 * these tests silently changes the emitted subclass for any file that sets
 * more than one bit.
 */
export function dxfPolylineSubtype(flags: number): DxfPolylineSubtype {
  if ((flags & DXF_POLYLINE_FLAG.POLYLINE_3D) !== 0) return "3d";
  if ((flags & DXF_POLYLINE_FLAG.POLYGON_MESH) !== 0) return "polygon-mesh";
  if ((flags & DXF_POLYLINE_FLAG.POLYFACE_MESH) !== 0) return "polyface-mesh";
  return "2d";
}

/**
 * Classify a single `VERTEX` from ITS OWN group-70 flags, mirroring ezdxf
 * `DXFVertex.export_entity`'s marker choice: 3D-polyline vertex (32) first,
 * then poly-face mesh vertex (64+128 BOTH set), then polygon-mesh vertex
 * (64), else a plain 2D vertex.
 *
 * Returns `"face-record"` for the special poly-face FACE record — flags with
 * bit 128 set but bit 64 clear (`is_face_record` in ezdxf), whose body is the
 * 71/72/73/74 vertex indices rather than a location.
 */
export function dxfVertexSubtype(
  flags: number | undefined,
): DxfPolylineSubtype | "face-record" {
  const f = flags ?? 0;
  const faceFlags =
    DXF_VERTEX_FLAG.POLYGON_MESH_VERTEX | DXF_VERTEX_FLAG.POLYFACE_MESH_VERTEX;
  if ((f & faceFlags) === DXF_VERTEX_FLAG.POLYFACE_MESH_VERTEX) {
    return "face-record";
  }
  if ((f & DXF_VERTEX_FLAG.POLYLINE_3D_VERTEX) !== 0) return "3d";
  if ((f & faceFlags) === faceFlags) return "polyface-mesh";
  if ((f & DXF_VERTEX_FLAG.POLYGON_MESH_VERTEX) !== 0) return "polygon-mesh";
  return "2d";
}
